import { Guid } from '@breezy/shared'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation } from 'urql'
import { DELETE_FAVORITE_PRICEBOOK_ITEM, INSERT_FAVORITE_PRICEBOOK_ITEM } from '../providers/PricebookProvider.gql'
import { useMessage } from '../utils/antd-utils'

type UseToggleFavoritePricebookItemArgs = {
  serverFavoriteGuids: Set<Guid>
  refetchFavorites: () => void
  userGuid: Guid
  companyGuid: Guid | undefined
}

export const useToggleFavoritePricebookItem = ({
  serverFavoriteGuids,
  refetchFavorites,
  userGuid,
  companyGuid,
}: UseToggleFavoritePricebookItemArgs) => {
  const [, insertFavorite] = useMutation(INSERT_FAVORITE_PRICEBOOK_ITEM)
  const [, deleteFavorite] = useMutation(DELETE_FAVORITE_PRICEBOOK_ITEM)
  const message = useMessage()

  // Tracks optimistic overrides: guid -> true (add) or false (remove)
  const [optimisticOverrides, setOptimisticOverrides] = useState<Record<Guid, boolean>>({})

  // Drop overrides that server state has caught up with
  useEffect(() => {
    setOptimisticOverrides(prev => {
      const stale = Object.entries(prev).filter(([guid, isFavorited]) =>
        isFavorited ? serverFavoriteGuids.has(guid) : !serverFavoriteGuids.has(guid),
      )
      if (stale.length === 0) return prev
      const next = { ...prev }
      for (const [guid] of stale) {
        delete next[guid]
      }
      return next
    })
  }, [serverFavoriteGuids])

  const favoriteGuids = useMemo(() => {
    const result = new Set(serverFavoriteGuids)
    for (const [guid, isFavorited] of Object.entries(optimisticOverrides)) {
      if (isFavorited) {
        result.add(guid)
      } else {
        result.delete(guid)
      }
    }
    return result
  }, [serverFavoriteGuids, optimisticOverrides])

  const toggleFavorite = useCallback(
    async (pricebookItemGuid: Guid) => {
      if (!companyGuid) return

      const isCurrentlyFavorited = favoriteGuids.has(pricebookItemGuid)

      // Apply optimistic override immediately
      setOptimisticOverrides(prev => ({
        ...prev,
        [pricebookItemGuid]: !isCurrentlyFavorited,
      }))

      const result = isCurrentlyFavorited
        ? await deleteFavorite({ pricebookItemGuid, userGuid })
        : await insertFavorite({
            object: {
              pricebookItemGuid,
              userGuid,
              companyGuid,
            },
          })

      if (result.error) {
        // Revert optimistic override on failure
        setOptimisticOverrides(prev => {
          const next = { ...prev }
          delete next[pricebookItemGuid]
          return next
        })
        message.error('Failed to update favorite')
        return
      }

      // On success, trigger a refetch. The optimistic override stays in place
      // until the refetch response arrives, preventing a UI flash where the
      // item briefly disappears from the favorites list.
      refetchFavorites()
    },
    [favoriteGuids, deleteFavorite, insertFavorite, userGuid, companyGuid, refetchFavorites, message],
  )

  return { favoriteGuids, toggleFavorite }
}
